import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import {
  CUSTOMERS_ENDPOINT,
  OASIS_CUSTOMERS_EXPORT_ENDPOINT,
} from "../../config/urls";
import CustomerFormDialog from "./CustomerFormDialog";
import { useAuth } from "../../hooks/AuthProvider";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import AppTable from "../../common/app-table";
import CustomerSearchInput from "../CustomerSearchInput";
import { resolveSearchQueryForApi } from "../../utils/phoneSearch";
import { formatInTenantTz, formatWithTzLabel } from "../../utils/dateUtils";

const buildCustomersSearchUrl = (tableSearch) => {
  if (!tableSearch?.value) return "";

  if (tableSearch.type === "customerId") {
    return `&search_id=${encodeURIComponent(tableSearch.value)}`;
  }

  const r = resolveSearchQueryForApi(tableSearch.value);
  if (r.blockFetch || !r.apiSearchQuery) return "";
  return `&search_value=${encodeURIComponent(r.apiSearchQuery)}`;
};

function CustomersTableOasis({ dateInput, tableSearch, updateSearchValue }) {
  const { getValidToken } = useAuth();
  const auth = useAuth();
  const [rowCount, setRowCount] = useState(0);
  const [customersData, setUsersData] = useState([]);
  const [customerModalState, setCustomerModalState] = useState({
    isOpen: false,
    selectedClient: null,
    mode: "add",
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
  });

  useEffect(() => {
    setController((prev) => ({ ...prev, page: 0 }));
  }, [tableSearch?.type, tableSearch?.value]);

  useEffect(() => {
    const searchURL = buildCustomersSearchUrl(tableSearch);

    const fetchData = async () => {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      try {
        const headers = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        };

        const response = await fetch(
          CUSTOMERS_ENDPOINT +
            `?limit=${controller.rowsPerPage}&offset=${controller.page * controller.rowsPerPage}` +
            searchURL,
          { headers },
        );

        if (!response.ok) {
          const errorData = await response.json();
          if (errorData.code === "user_inactive") {
            auth.logOut(); // Immediately logs out the user
            navigate("/login");
          }

          throw new Error("Failed to fetch data");
        }
        const jsonData = await response.json();
        setUsersData(jsonData.results);
        setRowCount(jsonData.count);
      } catch (error) {
        console.error("Error fetching data:", error.message);
      }
    };
    fetchData();
  }, [controller, refreshKey, tableSearch?.type, tableSearch?.value]);

  const handlePageChange = (event, newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setController({
      ...controller,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    });
  };

  const handleEditClick = (customer) => {
    console.log("Customer Data", customer);
    setCustomerModalState({
      isOpen: true,
      selectedClient: customer,
      mode: "edit",
    });
  };

  const handleExport = async () => {
    // Same search params as the table fetch, without limit/offset
    const searchURL = buildCustomersSearchUrl(tableSearch).replace(/^&/, "?");
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    setIsExporting(true);
    try {
      const response = await fetch(OASIS_CUSTOMERS_EXPORT_ENDPOINT + searchURL, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenant,
        },
      });
      if (!response.ok) throw new Error("Failed to export data");

      const blob = await response.blob();
      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = urlBlob;
      a.download = `customers-${dayjs().format("YYYY-MM-DD")}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(urlBlob);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting data:", error.message);
      alert("Failed to export customers. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleAddClick = () => {
    setCustomerModalState({ isOpen: true, selectedClient: null, mode: "add" });
  };

  const columns = [
    { id: "code", label: "Customer Code" },
    { id: "name", label: "Name" },
    { id: "phone_no", label: "Phone No" },
    { id: "email", label: "Email", render: (c) => c.email || "--" },
    {
      id: "date_of_birth",
      label: "Date of Birth",
      render: (c) =>
        c.date_of_birth && c.date_of_birth.trim() !== ""
          ? /^\d{4}-\d{2}-\d{2}$/.test(c.date_of_birth)
            ? dayjs(c.date_of_birth).format("DD-MM-YYYY")
            : c.date_of_birth
          : "--",
    },
    {
      id: "language_code",
      label: "Language",
      render: (c) => c.language_code.toUpperCase(),
    },
    {
      id: "created_at",
      label: "Created On",
      render: (c) =>
        c.created_at ? formatInTenantTz(c.created_at, "DD/MM/YYYY") : "--",
    },
    {
      id: "created_by",
      label: "Created By",
      render: (c) => c.created_by?.name,
    },
    {
      id: "updated_at",
      label: "Updated On",
      render: (c) =>
        c.updated_at ? formatWithTzLabel(c.updated_at, "DD/MM/YYYY") : "--",
    },
    {
      id: "updated_by",
      label: "Updated By",
      render: (c) => c.updated_by?.name || "--",
    },
    {
      id: "action",
      label: "Action",
      align: "center",
      render: (c) => (
        <ButtonOne variant="contained" onClick={() => handleEditClick(c)}>
          Edit
        </ButtonOne>
      ),
    },
  ];

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          flexDirection: "row",
          py: 1,
          my: 1,
          bgcolor: "background.paper",
          borderRadius: 1,
          gap: 2
        }}
      >
        <CustomerSearchInput
          updateSearchValue={updateSearchValue}
          searchInput={tableSearch?.value ?? ""}
        />
        <ButtonOne variant="contained" onClick={handleAddClick}>
          Add Customer
        </ButtonOne>
        <ButtonTwo onClick={handleExport} disabled={isExporting}>
          {isExporting ? "Exporting..." : "Export CSV"}
        </ButtonTwo>
      </Box>
      <AppTable
        columns={columns}
        rows={customersData}
        rowKey={(customer, index) => index}
        emptyMessage="No match found"
        maxHeight={440}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <CustomerFormDialog
        modalState={customerModalState}
        handleModalState={setCustomerModalState}
        selectedCustomer={customerModalState.selectedClient}
        refreshTable={setRefreshKey}
      />
    </Paper>
  );
}

export default CustomersTableOasis;
